import { z } from "zod";
/** API-only version negotiation; omission preserves the original simulation response. */
export const OPERATION_ST_REVIEW_HEADER = "x-tax-st-review";
export const operationStReviewVersionSchema = z.literal("ST_REVIEW_V1");
const ruleId = z.string().uuid();
const zeroMoney = z.string().regex(/^0(?:\.0{1,2})?$/);
/** Exact money spelling is retained. Digit presence establishes positivity without a float. */
const positiveMoney = z.string().regex(/^(?=[0-9.]*[1-9])(?:0|[1-9]\d{0,15})(?:\.\d{1,2})?$/);
export const operationStReviewInvoiceDeclarationSchema = z.discriminatedUnion("state", [
    z.object({ state: z.literal("NOT_DECLARED"), amount: z.null() }).strict(),
    z.object({ state: z.literal("DECLARED_ZERO"), amount: zeroMoney }).strict(),
    z.object({ state: z.literal("DECLARED_POSITIVE"), amount: positiveMoney }).strict(),
]);
export const operationStReviewTreatmentSchema = z.discriminatedUnion("state", [
    z.object({ state: z.literal("PUBLISHED_ST"), ruleId }).strict(),
    z.object({ state: z.literal("PUBLISHED_STANDARD"), ruleId }).strict(),
    z.object({ state: z.literal("PUBLISHED_ST_DISABLED"), ruleId }).strict(),
    z
        .object({
        state: z.literal("QUALIFIED_GENERAL_NON_ST"),
        source: z.literal("PURCHASE_GENERAL_RULE"),
        ruleId: z.null(),
    })
        .strict(),
    z
        .object({
        state: z.literal("UNKNOWN"),
        reason: z.enum([
            "REVIEW_REQUIRED",
            "RULE_NOT_SELECTED",
            "RULE_SNAPSHOT_MISMATCH",
            "LEGACY_NOT_TAXED",
            "UNQUALIFIED_TREATMENT",
            "DECLARATION_CONFLICT",
        ]),
        ruleId: ruleId.nullable(),
    })
        .strict(),
]);
export const operationStReviewResponsibilitySchema = z.discriminatedUnion("state", [
    z
        .object({
        state: z.literal("SENDER_SUPPORTED"),
        source: z.literal("RETURNED_SENDER_METADATA"),
    })
        .strict(),
    z
        .object({
        state: z.literal("UNKNOWN"),
        reason: z.enum([
            "NO_TRUSTED_SENDER_METADATA",
            "REVIEW_REQUIRED",
            "ROUTE_OR_RULE_CONFLICT",
            "DECLARATION_CONFLICT",
        ]),
    })
        .strict(),
]);
/** Sender support describes returned metadata, never proof of payment or cost readiness. */
export const operationStReviewItemSchema = z
    .object({
    itemId: z.string().min(1).max(100),
    declaration: z
        .object({
        priorStIncluded: z.enum(["NOT_DECLARED", "DECLARED_TRUE", "DECLARED_FALSE"]),
        invoiceSt: operationStReviewInvoiceDeclarationSchema,
    })
        .strict(),
    treatment: operationStReviewTreatmentSchema,
    responsibility: operationStReviewResponsibilitySchema,
    recipientAnticipation: z
        .object({
        state: z.literal("UNASSESSED"),
        reason: z.literal("QUALIFIED_RECIPIENT_SOURCE_REQUIRED"),
    })
        .strict(),
})
    .strict()
    .superRefine((item, context) => {
    if (item.responsibility.state === "SENDER_SUPPORTED" &&
        (item.treatment.state !== "PUBLISHED_ST" ||
            item.declaration.priorStIncluded === "DECLARED_TRUE"))
        context.addIssue({
            code: "custom",
            path: ["responsibility"],
            message: "ST_REVIEW_SENDER_METADATA_INCONSISTENT",
        });
})
    .meta({
    // JSON Schema equivalent of the item-level runtime refinement above.
    allOf: [
        {
            anyOf: [
                {
                    properties: {
                        responsibility: { properties: { state: { const: "UNKNOWN" } } },
                    },
                },
                {
                    properties: {
                        responsibility: { properties: { state: { const: "SENDER_SUPPORTED" } } },
                        treatment: { properties: { state: { const: "PUBLISHED_ST" } } },
                        declaration: {
                            properties: { priorStIncluded: { enum: ["NOT_DECLARED", "DECLARED_FALSE"] } },
                        },
                    },
                },
            ],
        },
    ],
});
/** Read-only projection of this response's selected snapshot; not a new tax resolver. */
export const operationStReviewSchema = z
    .object({
    version: operationStReviewVersionSchema,
    purpose: z.literal("REVIEW_ONLY"),
    calculationEffect: z.literal("NONE"),
    rulesetContentDigest: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    items: z.array(operationStReviewItemSchema).min(1).max(50),
})
    .strict()
    .superRefine((review, context) => {
    const seen = new Set();
    for (const [index, item] of review.items.entries()) {
        if (seen.has(item.itemId))
            context.addIssue({
                code: "custom",
                path: ["items", index, "itemId"],
                message: "ST_REVIEW_DUPLICATE_ITEM_ID",
            });
        seen.add(item.itemId);
    }
})
    .describe("Partial ST review only. Runtime enforces unique item IDs; the containing response also enforces exact response item IDs and ruleset digest equality. No calculation or recipient anticipation is qualified.");
//# sourceMappingURL=operation-st-review.js.map